import { Fragment, useState } from 'react';
import { AlertTriangle, Check, Plus, X } from 'lucide-react';
import type { RuleClause } from '@/hooks/useNotifications.ts';
import type { TagDefinition } from '@/hooks/useTagDefinitions';
import { useTagValuesByDefinition } from '@/hooks/useTagValues.ts';
import { tagValueLabel } from '@/components/tags/tagSelection.ts';
import {
  clauseGroupSentence,
  clauseTargetKey,
  findClauseConflicts,
  groupClauses,
  isTagKind,
  type ClauseConflict,
  type ConflictKind,
  type ScopeOption,
} from './ruleSentence.ts';
import { Button } from '@/components/ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command.tsx';

/**
 * The clause editor, shared by personal rules (§3.1) and organization routes (§3.2).
 *
 * Both are a conjunction of clauses over the same scope set and both read as
 * sentences, so the chip with its two toggles is the same control in both places.
 * Keeping one copy is not only less code — it is what stops the two surfaces from
 * drifting into different readings of "affecting" and "confined to", which is the
 * kind of divergence nobody notices until a route quietly matches the wrong
 * incidents.
 *
 * **A chip is a GROUP, not a row.** Clauses sharing a `groupId` OR together and groups
 * AND (`matcher::evaluate_group`), so one chip reads "affecting region Ashburn or
 * Dallas" and holds two clause rows. That is why the target is a multi-select rather
 * than a single pick: before groups existed, two regions meant two AND-ed clauses,
 * which no event can satisfy — a scope row has exactly one region — so the rule
 * silently matched nothing.
 */

/** A stable grouping token for a newly added chip. The server re-mints it on write. */
function newGroupId(): string {
  return crypto.randomUUID();
}

/**
 * Which axis a group selects on. Read from the group's first member, the same way the
 * matcher reads its quantifier — every member of a group shares it, and the backend
 * refuses a write where they do not (`validate_groups`).
 */
type GroupKind = 'tag' | 'division' | 'region';

function groupKind(first: RuleClause): GroupKind {
  if (first.tagDefinitionId) return 'tag';
  if (first.divisionId) return 'division';
  return 'region';
}

/**
 * Red means broken, amber means suspicious, and the difference has to survive a glance.
 *
 * A contradiction cannot match and a duplicate is the same thing twice — both are
 * errors. An overlap is a legal rule that probably says something narrower than its
 * author intended; colouring it the same red would train people to ignore red.
 */
const CHIP_TONE: Record<ConflictKind | 'none', string> = {
  contradiction: 'border-destructive bg-destructive/10 text-destructive',
  duplicate: 'border-destructive bg-destructive/10 text-destructive',
  overlap: 'border-warning bg-warning/10 text-warning',
  none: 'border-border bg-muted/40',
};

/**
 * A popover of checkable options — the one control every target picker uses.
 *
 * Stays open across picks, deliberately: choosing three regions is one intention, and
 * a menu that closes on each pick turns it into three trips.
 */
function MultiSelectMenu({
  label,
  options,
  selected,
  emptyText,
  onToggle,
}: {
  label: string;
  options: { id: string; name: string }[];
  selected: Set<string>;
  emptyText: string;
  onToggle: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="font-medium underline decoration-dotted underline-offset-2 hover:text-foreground"
          aria-label={`Edit ${label}`}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  onSelect={() => { onToggle(option.id); }}
                >
                  <Check className={selected.has(option.id) ? 'w-3.5 h-3.5 opacity-100' : 'w-3.5 h-3.5 opacity-0'} />
                  {option.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The values of one tag definition, multi-selected, with "any value" as a real choice.
 *
 * "Any value" is not the empty selection — it is the *broad* clause, a single member
 * with `tagValueId: null` that matches the definition however it is valued. Picking it
 * therefore collapses the group rather than clearing it, and picking a named value
 * replaces it. Letting the two coexist would store a redundant narrow member beside a
 * member that already subsumes it.
 */
function TagValueTargets({
  organizationId,
  definition,
  members,
  onChange,
}: {
  organizationId: string | undefined;
  definition: TagDefinition;
  members: RuleClause[];
  onChange: (members: RuleClause[]) => void;
}) {
  const { data: values } = useTagValuesByDefinition(
    organizationId,
    definition.valueType === 'enumerated' ? definition.id : null,
  );
  // Non-empty by construction: `groupClauses` never yields an empty group and
  // `ClauseList` never renders one, which is why this indexes rather than guards.
  const first = members[0];

  // Boolean tags carry no value at all — presence is the assertion — and a freeform
  // tag's values are not a list to choose from. Neither has a multi-select to offer.
  if (definition.valueType !== 'enumerated') return null;

  const ANY = '__any__';
  const selected = new Set(members.map((m) => m.tagValueId ?? ANY));
  const labels = new Map((values ?? []).map((v) => [v.id, tagValueLabel(v)]));
  const options = [{ id: ANY, name: 'any value' }, ...(values ?? []).map((v) => ({ id: v.id, name: tagValueLabel(v) }))];

  const label = selected.has(ANY)
    ? 'any value'
    : members.map((m) => (m.tagValueId ? (labels.get(m.tagValueId) ?? '…') : 'any value')).join(' or ');

  return (
    <MultiSelectMenu
      label={label}
      options={options}
      selected={selected}
      emptyText="This tag has no values yet."
      onToggle={(id) => {
        if (id === ANY) {
          onChange([{ ...first, tagValueId: null }]);
          return;
        }
        // Leaving "any value" behind the moment a specific one is chosen: the broad
        // member would swallow every narrow one and the chip would read as narrower
        // than it matches.
        const named = members.filter((m) => m.tagValueId);
        const without = named.filter((m) => m.tagValueId !== id);
        if (without.length !== named.length) {
          // Unchecking the last named value leaves nothing to match; fall back to the
          // broad clause rather than an empty group the server would reject.
          onChange(without.length > 0 ? without : [{ ...first, tagValueId: null }]);
          return;
        }
        onChange([...named, { ...first, tagValueId: id }]);
      }}
    />
  );
}

/**
 * Divisions or regions, multi-selected.
 *
 * A group must keep at least one member — an empty disjunction matches nothing and has
 * no sentence — so unchecking the last one is a no-op. Removing the condition entirely
 * is what the × is for, and conflating "narrow this to nothing" with "delete this"
 * would make the chip vanish under a click that reads as a correction.
 */
function ScopeTargets({
  kind,
  scopes,
  members,
  onChange,
}: {
  kind: Exclude<GroupKind, 'tag'>;
  scopes: ScopeOption[];
  members: RuleClause[];
  onChange: (members: RuleClause[]) => void;
}) {
  // Non-empty by construction — see `TagValueTargets`.
  const first = members[0];
  const field = kind === 'division' ? 'divisionId' : 'regionId';
  const options = scopes.filter((s) => s.kind === kind).map((s) => ({ id: s.id, name: s.name }));
  const selectedIds = members.map((m) => m[field]).filter((id): id is string => !!id);
  const selected = new Set(selectedIds);
  const names = selectedIds.map((id) => options.find((o) => o.id === id)?.name ?? 'unknown');

  return (
    <MultiSelectMenu
      label={names.join(' or ')}
      options={options}
      selected={selected}
      emptyText={kind === 'division' ? 'No divisions yet.' : 'No regions yet.'}
      onToggle={(id) => {
        if (selected.has(id)) {
          const without = members.filter((m) => m[field] !== id);
          if (without.length > 0) onChange(without);
          return;
        }
        onChange([...members, { ...first, [field]: id }]);
      }}
    />
  );
}

/** One chip: a group's two toggles, its set of targets, and a remove button. */
function ClauseGroupChip({
  members,
  scopes,
  organizationId,
  tagDefinitions,
  tagValueLabels,
  conflict,
  onMerge,
  onChange,
  onRemove,
}: {
  members: RuleClause[];
  scopes: ScopeOption[];
  organizationId: string | undefined;
  tagDefinitions: TagDefinition[];
  tagValueLabels: Record<string, string>;
  /** Set when another condition names the same target — see `findClauseConflicts`. */
  conflict?: ClauseConflict;
  /** Fold this condition together with everything in `conflict.mergeWith`. */
  onMerge?: () => void;
  onChange: (next: RuleClause[]) => void;
  onRemove: () => void;
}) {
  // Non-empty by construction — see `TagValueTargets`.
  const first = members[0];
  const kind = groupKind(first);
  const definition = first.tagDefinitionId ? tagDefinitions.find((d) => d.id === first.tagDefinitionId) : undefined;

  // The toggles belong to the GROUP, so they rewrite every member. The backend refuses
  // a group whose members disagree, and the matcher reads them off the first row — a
  // chip that flipped only the row it was rendered from would produce exactly that.
  const setAll = (patch: Partial<RuleClause>) => { onChange(members.map((m) => ({ ...m, ...patch }))); };

  // An event has at most one alert definition, so "affecting" and "confined to" ask
  // the same question and the matcher does not read the quantifier on this axis. A
  // toggle here would be a control with no effect — the shape §10 warns against.
  const quantified = first.tagScope !== 'alert_definition';

  return (
    <span
      // Flagged, not blocked. Every one of these rules still saves: the conditions are
      // legal, they are just probably not what the author meant, and a composer that
      // refuses a legal rule on a guess is worse than one that says so and steps aside.
      className={`inline-flex items-center gap-1.5 rounded-full border pl-2 pr-1 py-1 text-xs ${CHIP_TONE[conflict?.kind ?? 'none']}`}
      // The colour alone says "wrong" without saying what or what to do, and it is
      // invisible to anyone who cannot distinguish it. `role="status"` puts the same
      // sentence in the accessibility tree.
      role={conflict ? 'status' : undefined}
      title={conflict?.message}
    >
      {conflict ? <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" /> : null}
      {quantified ? (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2"
          title="Switch between 'affecting' (touches this scope at all) and 'confined to' (touches nothing else)"
          onClick={() => { setAll({ quantifier: first.quantifier === 'any' ? 'all' : 'any' }); }}
        >
          {first.quantifier === 'all' ? 'confined to' : 'affecting'}
        </button>
      ) : null}
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2"
        title="Negate this condition"
        onClick={() => { setAll({ negated: !first.negated }); }}
      >
        {first.negated ? 'is not' : 'is'}
      </button>

      {kind === 'tag' && definition ? (
        // The value set is a control, not text, so the chip builds its own phrase
        // instead of rendering the sentence and then covering half of it.
        // `clauseGroupSentence` stays the single reader for surfaces with no controls:
        // the list and the preview.
        <span className="font-medium">
          {first.tagScope === 'alert_definition' ? 'from alerts tagged ' : 'devices tagged '}
          {definition.key}
          {definition.valueType === 'enumerated' ? ': ' : ''}
          <TagValueTargets organizationId={organizationId} definition={definition} members={members} onChange={onChange} />
        </span>
      ) : kind === 'tag' ? (
        // A definition the org can no longer offer — deleted, or not selectable on this
        // axis. Read-only rather than a picker with nothing in it.
        <span className="font-medium">{clauseGroupSentence(members, scopes, tagValueLabels).replace(/^(not )?(affecting |confined to |from )/, '')}</span>
      ) : (
        <span className="font-medium">
          {kind === 'division' ? 'division ' : 'region '}
          <ScopeTargets kind={kind} scopes={scopes} members={members} onChange={onChange} />
        </span>
      )}

      {conflict && conflict.mergeWith.length > 0 && onMerge ? (
        // Offered only where it is meaningful. Two conditions with opposite `is` cannot
        // share a group — negation belongs to the group — so a contradiction has nothing
        // to collapse and gets no button; which of the two to drop is the author's call,
        // not a default we can guess.
        <button
          type="button"
          className="rounded-full border border-current/60 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide hover:bg-current/15"
          title="Combine these conditions into one, matching any of their targets"
          onClick={onMerge}
        >
          Merge
        </button>
      ) : null}
      <button type="button" className="text-muted-foreground hover:text-destructive p-0.5" onClick={onRemove} aria-label="Remove condition">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

/**
 * Pick one division or region.
 *
 * Exported because the simulator needs the same picker without the clause chip's
 * quantifier and negation toggles — a simulated event's scope *set* has no "affecting
 * ⇄ confined to" of its own; that distinction belongs to the rule reading it.
 */
export function ScopePicker({
  scopes,
  onPick,
  label = 'Add condition',
  inUse,
}: {
  scopes: ScopeOption[];
  onPick: (scope: ScopeOption) => void;
  label?: string;
  /**
   * Targets the rule already narrows on, keyed by [`clauseTargetKey`]. Marked rather
   * than removed, deliberately: hiding them would make real policies unwriteable — you
   * could no longer say "affecting region Ashburn and not affecting region Lab", and
   * "not confined to the lab" is the spec's own worked example for the quantifier. The
   * mistake being guarded against is not noticing, so being told is the whole fix.
   *
   * Omitted by the simulator, which uses this picker to build a synthetic event's scope
   * SET — a different job, where "already used" would mean nothing.
   */
  inUse?: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const group = (kind: ScopeOption['kind']) => scopes.filter((s) => s.kind === kind);

  // One popover with four groups rather than a second "Add tag" button beside the
  // first. The axis is chosen here, at creation, because it is not something you flip:
  // it decides which object the clause asks about, and the quantifier is meaningless
  // on one of the two. A toggle would have to appear and disappear as you used it.
  const groups: { kind: ScopeOption['kind']; heading: string }[] = [
    { kind: 'division', heading: 'Divisions' },
    { kind: 'region', heading: 'Regions' },
    { kind: 'deviceTag', heading: 'Tags on affected devices, regions, or divisions' },
    { kind: 'alertTag', heading: 'Tags on the alert that fired' },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs">
          <Plus className="w-3 h-3" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command>
          <CommandInput placeholder="Division, region or tag…" />
          <CommandList>
            <CommandEmpty>Nothing to scope to yet.</CommandEmpty>
            {groups.map(({ kind, heading }) =>
              group(kind).length === 0 ? null : (
                <CommandGroup key={kind} heading={heading}>
                  {group(kind).map((s) => (
                    // The kind is in the search value so "region ashburn" narrows the
                    // way the headings imply, and so a tag and a region of the same
                    // name stay distinguishable while typing.
                    <CommandItem key={`${kind}-${s.id}`} value={`${kind} ${s.name}`} onSelect={() => { onPick(s); setOpen(false); }}>
                      {s.name}
                      {inUse?.has(`${kind}:${s.id}`) ? (
                        // Says what picking again will DO, not merely that the target is
                        // spoken for. "In use" alone would leave the reader to guess
                        // whether the second pick widens the rule or narrows it — and
                        // guessing "widens" is exactly the mistake this marker exists to
                        // prevent.
                        <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                          in use · adds AND
                        </span>
                      ) : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ),
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The whole "where" row: the current condition groups, plus the control that adds one.
 *
 * Takes the flat clause list and returns the next flat list, so neither caller has to
 * reimplement the group-wise replace/remove — the place a copy would most easily go
 * subtly wrong now that one chip owns several rows.
 */
export function ClauseList({
  clauses,
  scopes,
  organizationId,
  tagDefinitions,
  tagValueLabels,
  onChange,
}: {
  clauses: RuleClause[];
  scopes: ScopeOption[];
  organizationId: string | undefined;
  tagDefinitions: TagDefinition[];
  tagValueLabels: Record<string, string>;
  onChange: (next: RuleClause[]) => void;
}) {
  const groups = groupClauses(clauses);

  // Rebuilt from the grouping rather than spliced by index: `groupClauses` preserves
  // first-appearance order, so mapping groups back to a flat list round-trips exactly,
  // and a group's members stay adjacent whatever the caller does next.
  const replaceGroup = (index: number, members: RuleClause[]) => {
    onChange(groups.flatMap((g, i) => (i === index ? members : g.members)));
  };

  // Every target the rule already narrows on, so the picker can mark them rather than
  // hide them. Built from the flat list, not per group: a target used anywhere counts.
  const inUse = new Set(clauses.map(clauseTargetKey).filter((key): key is string => key !== null));

  // Computed across ALL groups, so both chips of a conflicting pair are marked. Marking
  // only the later one would read as "this one is wrong", when the fix may well be to
  // remove the earlier one — or to merge the two into a single OR condition.
  const conflicts = findClauseConflicts(groups);

  /**
   * Fold `keys` into one condition, matching any of their targets.
   *
   * The merged group keeps the FIRST group's position and reading — merging is meant to
   * be a tidy-up, and a condition that jumped to the end of the row would read as a new
   * one. `quantifier`, `negated` and `tagScope` are already equal across the set, since
   * `findClauseConflicts` only pairs groups that agree on them.
   *
   * Deduped on the exact target, tag value included: two chips naming the same thing
   * collapse to one member rather than to a group that lists it twice.
   */
  const mergeGroups = (keys: string[]) => {
    const merged = new Set(keys);
    const survivors = groups.filter((g) => merged.has(g.key));
    // Non-empty by construction: `keys` always contains the chip the button is on.
    const first = survivors[0];

    // A real grouping token even when the first group had none: an ungrouped clause
    // carries no `groupId`, and leaving the merged members without one would put each
    // back in its own group — silently undoing the merge on the next render.
    const groupId = first.members[0]?.groupId ?? newGroupId();
    const seen = new Set<string>();
    const members: RuleClause[] = [];
    for (const member of survivors.flatMap((g) => g.members)) {
      const key = `${member.divisionId ?? ''}|${member.regionId ?? ''}|${member.tagDefinitionId ?? ''}|${member.tagValueId ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      members.push({ ...member, groupId });
    }

    onChange(groups.flatMap((g) => (g.key === first.key ? members : merged.has(g.key) ? [] : g.members)));
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {groups.map(({ key, members }, index) => (
        <Fragment key={key}>
          {/* Groups AND; the targets INSIDE a chip already read "Ashburn or Lab". Saying
              "and" here completes the grammar — or inside, and between — and it is the
              only thing on screen that does. Before this, adjacent chips looked like a
              list, and a list of conditions reads as alternatives to most people, which
              is the misreading that quietly over-narrows a rule until it matches
              nothing. */}
          {index > 0 ? <span className="text-xs font-medium text-muted-foreground select-none">and</span> : null}
          <ClauseGroupChip
            members={members}
            conflict={conflicts.get(key)}
            onMerge={() => { mergeGroups([key, ...(conflicts.get(key)?.mergeWith ?? [])]); }}
            scopes={scopes}
            organizationId={organizationId}
            tagDefinitions={tagDefinitions}
            tagValueLabels={tagValueLabels}
            onChange={(next) => { replaceGroup(index, next); }}
            onRemove={() => { onChange(groups.filter((_, i) => i !== index).flatMap((g) => g.members)); }}
          />
        </Fragment>
      ))}
      <ScopePicker
        inUse={inUse}
        scopes={scopes}
        onPick={(scope) => {
          const isTag = isTagKind(scope.kind);
          onChange([
            ...clauses,
            {
              // Its own group. Adding a second condition means AND — the user widens it
              // to an OR by adding targets *inside* the chip, which is the distinction
              // the two controls exist to keep visible.
              groupId: newGroupId(),
              divisionId: scope.kind === 'division' ? scope.id : null,
              regionId: scope.kind === 'region' ? scope.id : null,
              tagDefinitionId: isTag ? scope.id : null,
              // Born at "any value", which is a complete clause on its own: the value
              // narrows it, and requiring one up front would block the common case
              // ("anything tagged pci-scope") behind a choice with no right answer.
              tagValueId: null,
              tagScope: scope.kind === 'alertTag' ? 'alert_definition' : 'scope',
              quantifier: 'any',
              negated: false,
            },
          ]);
        }}
      />
    </div>
  );
}
